import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import userService from '../services/userService';
import channelService from '../services/channelService';
import { toast } from 'react-toastify';
import './Dashboard.css';

function Dashboard() {
  const { currentUser } = useAuth();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verificationToken, setVerificationToken] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [updatingChannels, setUpdatingChannels] = useState({});
  const [editingChannel, setEditingChannel] = useState(null);
  const [fetchingInfo, setFetchingInfo] = useState(false);
  const [resyncingChannel, setResyncingChannel] = useState(null);
  const [addingChannel, setAddingChannel] = useState(false);
  
  // New channel form state
  const [newChannel, setNewChannel] = useState({
    url: '',
    title: '',
    description: '',
    is_public: false
  });
  
  // Edit channel form state
  const [editChannel, setEditChannel] = useState({
    id: null,
    url: '',
    title: '',
    description: '',
    is_public: false
  });
  
  // Edit profile form state
  const [profile, setProfile] = useState({
    name: currentUser?.name || '',
    description: currentUser?.description || '',
    telegram_username: currentUser?.telegram_username || ''
  });

  // Debounced version of the fetch function
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get verification token regardless of verification status
      try {
        const tokenData = await userService.getVerificationToken();
        if (tokenData && tokenData.token) {
          setVerificationToken(tokenData.token);
          // Show the token automatically only if not verified
          setShowVerification(!currentUser.is_verified);
        }
      } catch (tokenErr) {
        toast.error('Failed to retrieve verification token');
      }
      
      // Get channels
      try {
        const channelsData = await channelService.getChannels();
        setChannels(channelsData);
      } catch (channelsErr) {
        toast.error('Failed to load channels');
      }
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' 
        ? err.response.data.detail 
        : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const handleAddChannel = async (e) => {
    e.preventDefault();
    
    try {
      setAddingChannel(true);
      const response = await channelService.createChannel(newChannel);
      setChannels([...channels, response]);
      setNewChannel({
        url: '',
        title: '',
        description: '',
        is_public: false
      });
      toast.success('Channel added successfully!');
    } catch (err) {
      // Check specifically for already exists error
      if (err.response?.status === 400 && 
          err.response?.data?.detail && 
          err.response?.data?.detail.includes("already exists")) {
        toast.error(`Channel with URL "${newChannel.url}" already exists in your collection.`);
      } else {
        // Handle other errors
        toast.error(typeof err.response?.data?.detail === 'string' 
          ? err.response.data.detail 
          : 'Failed to add channel');
      }
    } finally {
      setAddingChannel(false);
    }
  };
  
  const handleFetchChannelInfo = async () => {
    if (!newChannel.url) {
      toast.error('Please enter a Telegram channel URL');
      return;
    }
    
    try {
      setFetchingInfo(true);
      const info = await channelService.fetchChannelInfo(newChannel.url);
      
      setNewChannel({
        ...newChannel,
        title: info.title || newChannel.title,
        description: info.description || newChannel.description,
      });
      
      toast.success('Channel information fetched successfully');
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' 
        ? err.response.data.detail 
        : 'Failed to fetch channel information');
    } finally {
      setFetchingInfo(false);
    }
  };
  
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    try {
      await userService.updateProfile(profile);
      toast.success('Profile updated successfully!');
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' 
        ? err.response.data.detail 
        : 'Failed to update profile');
    }
  };
  
  const handleTogglePublic = async (channel) => {
    try {
      setUpdatingChannels(prev => ({ ...prev, [channel.id]: true }));
      const updatedChannel = await channelService.updateChannel(channel.id, {
        is_public: !channel.is_public
      });
      
      setChannels(channels.map(ch => 
        ch.id === updatedChannel.id ? updatedChannel : ch
      ));
      
      toast.success(`Channel visibility ${updatedChannel.is_public ? 'made public' : 'made private'}`);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error(`Channel not found. It may have been deleted.`);
        // Remove the non-existent channel from the list
        setChannels(channels.filter(ch => ch.id !== channel.id));
      } else {
        toast.error(typeof err.response?.data?.detail === 'string' 
          ? err.response.data.detail 
          : 'Failed to update channel');
      }
    } finally {
      setUpdatingChannels(prev => ({ ...prev, [channel.id]: false }));
    }
  };
  
  const handleStartEditChannel = (channel) => {
    setEditingChannel(channel.id);
    setEditChannel({
      id: channel.id,
      url: channel.url,
      title: channel.title || '',
      description: channel.description || '',
      is_public: channel.is_public
    });
  };
  
  const handleCancelEdit = () => {
    setEditingChannel(null);
    setEditChannel({
      id: null,
      url: '',
      title: '',
      description: '',
      is_public: false
    });
  };
  
  const handleUpdateChannel = async (e) => {
    e.preventDefault();
    
    try {
      setUpdatingChannels(prev => ({ ...prev, [editChannel.id]: true }));
      const updatedChannel = await channelService.updateChannel(editChannel.id, {
        url: editChannel.url,
        title: editChannel.title,
        description: editChannel.description,
        is_public: editChannel.is_public
      });
      
      setChannels(channels.map(ch => 
        ch.id === updatedChannel.id ? updatedChannel : ch
      ));
      
      setEditingChannel(null);
      setEditChannel({
        id: null,
        url: '',
        title: '',
        description: '',
        is_public: false
      });
      
      toast.success('Channel updated successfully');
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error(`Channel not found. It may have been deleted.`);
        // Remove the non-existent channel from the list
        setChannels(channels.filter(ch => ch.id !== editChannel.id));
      } else {
        toast.error(typeof err.response?.data?.detail === 'string' 
          ? err.response.data.detail 
          : 'Failed to update channel');
      }
    } finally {
      setUpdatingChannels(prev => ({ ...prev, [editChannel.id]: false }));
    }
  };
  
  const handleDeleteChannel = async (id) => {
    if (window.confirm('Are you sure you want to delete this channel?')) {
      try {
        await channelService.deleteChannel(id);
        setChannels(channels.filter(channel => channel.id !== id));
        toast.success('Channel deleted successfully');
      } catch (err) {
        if (err.response?.status === 404) {
          toast.info('Channel not found. It may have been already deleted.');
          // Remove the non-existent channel from the list
          setChannels(channels.filter(channel => channel.id !== id));
        } else {
          toast.error(typeof err.response?.data?.detail === 'string' 
            ? err.response.data.detail 
            : 'Failed to delete channel');
        }
      }
    }
  };
  
  const handleResyncChannel = async (id) => {
    try {
      setResyncingChannel(id);
      const updatedChannel = await channelService.resyncChannel(id);
      
      setChannels(channels.map(ch => 
        ch.id === updatedChannel.id ? updatedChannel : ch
      ));
      
      toast.success('Channel information updated successfully');
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('Channel not found. It may have been deleted.');
        // Remove the non-existent channel from the list
        setChannels(channels.filter(ch => ch.id !== id));
      } else {
        toast.error(typeof err.response?.data?.detail === 'string' 
          ? err.response.data.detail 
          : 'Failed to update channel information');
      }
    } finally {
      setResyncingChannel(null);
    }
  };
  
  if (loading) {
    return <div className="dashboard-loading">Loading dashboard...</div>;
  }
  
  return (
    <div className="dashboard">
      <div className="dashboard-grid">
        <div className="dashboard-section">
          <div className="card verification-card">
            <div className="card-header">
              <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              <h2>{currentUser.is_verified ? 'Account Verification' : 'Verify Your Account'}</h2>
            </div>
            <div className="card-body">
              <p>
                {currentUser.is_verified 
                  ? 'Your account is verified! You can still access your token below if needed.' 
                  : 'To start adding channels automatically, you need to verify your Telegram account.'}
              </p>
              
              <button 
                className="btn btn-secondary"
                onClick={() => setShowVerification(!showVerification)}
              >
                {showVerification ? 'Hide Token' : 'Show Verification Token'}
              </button>
              
              {showVerification && (
                <div className="verification-token">
                  <p>Send this token to our Telegram bot:</p>
                  {verificationToken ? (
                    <>
                      <div className="token">{verificationToken}</div>
                      <a 
                        href="https://t.me/channlverificationbot" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="btn btn-primary"
                      >
                        <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21.198 2.433a2.242 2.242 0 0 0-1.022.215l-8.609 3.33c-2.068.8-4.133 1.598-5.724 2.21a405.15 405.15 0 0 1-2.849 1.09c-.42.147-.99.332-1.473.901-.728.968.193 1.798.919 2.286 1.61.516 3.275 1.009 4.654 1.472.846 1.467 1.683 2.986 2.561 4.41.362.77 1.194.772 1.564.03.736-1.471 1.321-2.714 1.94-3.979.104-.21.202-.108.355.1 2.354 3.552 5.006 6.86 6.843 8.738.472.479 1.7.463 2.198-.003.497-.467.46-1.266.052-1.744-2.432-2.97-4.818-6.135-7.097-9.47a169.91 169.91 0 0 0-.54-.666c.223-.1.442-.196.653-.289 2.686-1.202 5.74-2.56 8.159-3.738.169-.086.565-.333.518-.839-.038-.508-.49-.655-.802-.762z" />
                        </svg>
                        Open Telegram Bot
                      </a>
                    </>
                  ) : (
                    <p>Loading token... If this takes too long, please refresh the page.</p>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div className="card">
            <div className="card-header">
              <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              <h2>Add New Channel</h2>
            </div>
            <div className="card-body">
              <form onSubmit={handleAddChannel}>
                <div className="form-group">
                  <label htmlFor="url">Channel URL</label>
                  <div className="input-with-button">
                    <input
                      type="text"
                      id="url"
                      value={newChannel.url}
                      onChange={(e) => setNewChannel({...newChannel, url: e.target.value})}
                      placeholder="https://t.me/yourchannel"
                      required
                    />
                    <button 
                      type="button" 
                      className="btn btn-secondary" 
                      onClick={handleFetchChannelInfo}
                      disabled={fetchingInfo || !newChannel.url}
                    >
                      {fetchingInfo ? (
                        <span className="loading-spinner-small"></span>
                      ) : (
                        'Fetch Info'
                      )}
                    </button>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="title">Title</label>
                  <input
                    type="text"
                    id="title"
                    value={newChannel.title}
                    onChange={(e) => setNewChannel({...newChannel, title: e.target.value})}
                    placeholder="Channel Title"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    value={newChannel.description}
                    onChange={(e) => setNewChannel({...newChannel, description: e.target.value})}
                    placeholder="Channel Description"
                    rows="3"
                  ></textarea>
                </div>
                
                <div className="form-group checkbox-group">
                  <input
                    type="checkbox"
                    id="is_public"
                    checked={newChannel.is_public}
                    onChange={(e) => setNewChannel({...newChannel, is_public: e.target.checked})}
                  />
                  <label htmlFor="is_public">Make this channel public on my profile</label>
                </div>
                
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={addingChannel}
                >
                  {addingChannel ? (
                    <>
                      <span className="loading-spinner-small"></span>
                      <span>Adding...</span>
                    </>
                  ) : (
                    'Add Channel'
                  )}
                </button>
              </form>
            </div>
          </div>
          
          <div className="card">
            <div className="card-header">
              <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              <h2>Edit Profile</h2>
            </div>
            <div className="card-body">
              <form onSubmit={handleUpdateProfile}>
                <div className="form-group">
                  <label htmlFor="name">Name</label>
                  <input
                    type="text"
                    id="name"
                    value={profile.name}
                    onChange={(e) => setProfile({...profile, name: e.target.value})}
                    placeholder="Your Name"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="telegram_username">Telegram Username</label>
                  <input
                    type="text"
                    id="telegram_username"
                    value={profile.telegram_username}
                    onChange={(e) => setProfile({...profile, telegram_username: e.target.value})}
                    placeholder="Your Telegram Username (without @)"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="profile_description">Bio</label>
                  <textarea
                    id="profile_description"
                    value={profile.description}
                    onChange={(e) => setProfile({...profile, description: e.target.value})}
                    placeholder="Tell us about yourself"
                    rows="3"
                  ></textarea>
                </div>
                
                <button type="submit" className="btn btn-primary">Update Profile</button>
              </form>
            </div>
          </div>
        </div>
        
        <div className="dashboard-section channels-section">
          <div className="section-header">
            <h2>My Channels</h2>
            <span className="channel-count">{channels.length} channel{channels.length !== 1 ? 's' : ''}</span>
          </div>
          
          {channels.length === 0 ? (
            <div className="empty-state">
              <svg viewBox="0 0 24 24" width="48" height="48" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="12" y1="8" x2="12" y2="16"></line>
                <line x1="8" y1="12" x2="16" y2="12"></line>
              </svg>
              <p>You haven't added any channels yet</p>
              <p className="empty-state-tip">Start by adding your first Telegram channel</p>
            </div>
          ) : (
            <div className="channels-list">
              {channels.map(channel => (
                <div key={channel.id} className="channel-item">
                  {editingChannel === channel.id ? (
                    <div className="channel-edit-form">
                      <form onSubmit={handleUpdateChannel}>
                        <div className="form-group">
                          <label htmlFor={`edit-url-${channel.id}`}>Channel URL</label>
                          <input
                            type="text"
                            id={`edit-url-${channel.id}`}
                            value={editChannel.url}
                            onChange={(e) => setEditChannel({...editChannel, url: e.target.value})}
                            placeholder="https://t.me/yourchannel"
                            required
                          />
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor={`edit-title-${channel.id}`}>Title</label>
                          <input
                            type="text"
                            id={`edit-title-${channel.id}`}
                            value={editChannel.title}
                            onChange={(e) => setEditChannel({...editChannel, title: e.target.value})}
                            placeholder="Channel Title"
                          />
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor={`edit-description-${channel.id}`}>Description</label>
                          <textarea
                            id={`edit-description-${channel.id}`}
                            value={editChannel.description}
                            onChange={(e) => setEditChannel({...editChannel, description: e.target.value})}
                            placeholder="Channel Description"
                            rows="3"
                          ></textarea>
                        </div>
                        
                        <div className="form-group checkbox-group">
                          <input
                            type="checkbox"
                            id={`edit-is-public-${channel.id}`}
                            checked={editChannel.is_public}
                            onChange={(e) => setEditChannel({...editChannel, is_public: e.target.checked})}
                          />
                          <label htmlFor={`edit-is-public-${channel.id}`}>Make this channel public on my profile</label>
                        </div>
                        
                        <div className="edit-actions">
                          <button type="submit" className="btn btn-primary" disabled={updatingChannels[channel.id]}>
                            {updatingChannels[channel.id] ? 'Saving...' : 'Save Changes'}
                          </button>
                          <button type="button" className="btn btn-secondary" onClick={handleCancelEdit}>Cancel</button>
                        </div>
                      </form>
                    </div>
                  ) : (
                    <>
                      <div className="channel-icon">
                        {channel.avatar_url ? (
                          <img src={channel.avatar_url} alt={channel.title || 'Channel'} className="channel-avatar" />
                        ) : (
                          <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21.198 2.433a2.242 2.242 0 0 0-1.022.215l-8.609 3.33c-2.068.8-4.133 1.598-5.724 2.21a405.15 405.15 0 0 1-2.849 1.09c-.42.147-.99.332-1.473.901-.728.968.193 1.798.919 2.286 1.61.516 3.275 1.009 4.654 1.472.846 1.467 1.683 2.986 2.561 4.41.362.77 1.194.772 1.564.03.736-1.471 1.321-2.714 1.94-3.979.104-.21.202-.108.355.1 2.354 3.552 5.006 6.86 6.843 8.738.472.479 1.7.463 2.198-.003.497-.467.46-1.266.052-1.744-2.432-2.97-4.818-6.135-7.097-9.47a169.91 169.91 0 0 0-.54-.666c.223-.1.442-.196.653-.289 2.686-1.202 5.74-2.56 8.159-3.738.169-.086.565-.333.518-.839-.038-.508-.49-.655-.802-.762z" />
                          </svg>
                        )}
                      </div>
                      
                      <div className="channel-details">
                        <div className="channel-top">
                          <h3>{channel.title || 'Untitled Channel'}</h3>
                          <div className={`channel-badge ${channel.is_public ? 'public' : 'private'}`}>
                            {channel.is_public ? 'Public' : 'Private'}
                          </div>
                        </div>
                        
                        <a href={channel.url} target="_blank" rel="noopener noreferrer" className="channel-url">
                          {channel.url}
                        </a>
                        
                        {channel.description && (
                          <p className="channel-description">{channel.description}</p>
                        )}
                        
                        {channel.subscribers_count && (
                          <p className="channel-subscribers">
                            <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                              <circle cx="9" cy="7" r="4"></circle>
                              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                            </svg>
                            {channel.subscribers_count.toLocaleString()} subscribers
                          </p>
                        )}
                        
                        <div className="channel-controls">
                          <button 
                            className="control-btn edit-btn"
                            onClick={() => handleStartEditChannel(channel)}
                            title="Edit channel"
                          >
                            <span className="btn-label">Edit</span>
                            <svg className="edit-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2"></path>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2"></path>
                            </svg>
                          </button>
                          
                          <button 
                            className={`control-btn visibility-btn ${updatingChannels[channel.id] ? 'loading' : ''}`}
                            onClick={() => handleTogglePublic(channel)}
                            title={channel.is_public ? "Make private" : "Make public"}
                            disabled={updatingChannels[channel.id]}
                          >
                            <span className="btn-label">{channel.is_public ? "Make Private" : "Make Public"}</span>
                            {updatingChannels[channel.id] ? (
                              <svg className="loading-spinner" viewBox="0 0 24 24" width="18" height="18">
                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="30" strokeDashoffset="20">
                                  <animateTransform
                                    attributeName="transform"
                                    attributeType="XML"
                                    type="rotate"
                                    from="0 12 12"
                                    to="360 12 12"
                                    dur="1s"
                                    repeatCount="indefinite"
                                  />
                                </circle>
                              </svg>
                            ) : channel.is_public ? (
                              <svg className="visibility-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2"></path>
                                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"></circle>
                              </svg>
                            ) : (
                              <svg className="visibility-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" strokeWidth="2"></path>
                                <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2"></line>
                              </svg>
                            )}
                          </button>
                          
                          <button 
                            className={`control-btn resync-btn ${resyncingChannel === channel.id ? 'loading' : ''}`}
                            onClick={() => handleResyncChannel(channel.id)}
                            title="Refresh channel information"
                            disabled={resyncingChannel === channel.id}
                          >
                            <span className="btn-label">Resync</span>
                            {resyncingChannel === channel.id ? (
                              <svg className="loading-spinner" viewBox="0 0 24 24" width="18" height="18">
                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="30" strokeDashoffset="20">
                                  <animateTransform
                                    attributeName="transform"
                                    attributeType="XML"
                                    type="rotate"
                                    from="0 12 12"
                                    to="360 12 12"
                                    dur="1s"
                                    repeatCount="indefinite"
                                  />
                                </circle>
                              </svg>
                            ) : (
                              <svg className="resync-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M23 4v6h-6" stroke="currentColor" strokeWidth="2"></path>
                                <path d="M1 20v-6h6" stroke="currentColor" strokeWidth="2"></path>
                                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" stroke="currentColor" strokeWidth="2"></path>
                              </svg>
                            )}
                          </button>
                          
                          <button 
                            className="control-btn delete-btn"
                            onClick={() => handleDeleteChannel(channel.id)}
                            title="Delete channel"
                          >
                            <span className="btn-label">Delete</span>
                            <svg className="delete-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2"></path>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard; 